// @ts-nocheck
/**
 * useEditorPicks - Editor Picks (AI-based Product Recommendations) Hook
 * 
 * Extracted from App.tsx to manage:
 * - editorPicks state (ranked product recommendations)
 * - isEditorPicksLoading state
 * - handleRefreshEditorPicks callback
 * - Auto-refresh for Phase 2 users (AI analysis complete + 50% profile)
 * - Analytics tracking for recommendation views
 * 
 * Monetization Modes:
 * - 'affiliate': Use Amazon affiliate products (via Google Search API)
 * - 'inhouse': Use in-house catalog products (original behavior)
 * 
 * Dependencies:
 * - profileService: rankEditorPicks, ensureShape, calculateProfileCompletion
 * - eventTrackingService: trackRecommendationViewed
 * - firebase auth: current user for tracking
 * - adminConfig: MONETIZATION_MODE setting
 * 
 * Usage in App.tsx:
 * ```tsx
 * const {
 *   editorPicks,
 *   isEditorPicksLoading,
 *   handleRefreshEditorPicks,
 *   handleRefreshEditorPicksRef,
 * } = useEditorPicks({
 *   masterProfile,
 *   catalog,
 *   isLoggedIn,
 *   t,
 *   setProfileError,
 * });
 * ```
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { auth } from '../services/firebaseConfig';
import {
  rankEditorPicks,
  ensureShape,
  calculateProfileCompletion,
} from '../services/profileService';
import {
  hasEmbeddings,
  getProfileEmbedding,
  getEmbeddingSimilarityMap,
} from '../services/embeddingService';
import { trackRecommendationViewed } from '../services/eventTrackingService';
import { getAuthHeaders } from '../services/apiAuth';
import { getApiBaseUrl, nativeFetch } from '../utils/apiConfig';
import { MONETIZATION_MODE, type MonetizationMode } from '../config/adminConfig';
import { getRegionConfig, detectUserRegion, getSearchParams, getAmazonTagForRegion, type SupportedRegion } from '../config/affiliateConfig';
import {
  distributeSlots,
  buildAffiliateUrl,
  extractProductId,
  DEFAULT_NETWORKS,
  type NetworkConfig,
} from '../utils/affiliateDistribution';
import { getCached, setCached, buildProfileCacheKey } from '../utils/affiliateSearchCache';

interface UseEditorPicksParams {
  masterProfile: any;
  catalog: any[];
  isLoggedIn: boolean;
  t?: (ko: string, en: string) => string; // Deprecated: now using i18n internally
  setProfileError: (error: string | null) => void;
  /** Override monetization mode (default: MONETIZATION_MODE constant) */
  monetizationMode?: MonetizationMode;
  /** Whether to use RAG vector similarity in inhouse mode (default: true) */
  useRag?: boolean;
}

interface UseEditorPicksReturn {
  editorPicks: any[];
  setEditorPicks: React.Dispatch<React.SetStateAction<any[]>>;
  isEditorPicksLoading: boolean;
  handleRefreshEditorPicks: (profileOverride?: any) => Promise<void>;
  handleRefreshEditorPicksRef: React.MutableRefObject<((profileOverride?: any) => Promise<void>) | null>;
  /** Whether current picks are from affiliate sources */
  isAffiliate: boolean;
}

// iHerb affiliate code - from env var
const IHERB_AFFILIATE_CODE = import.meta.env.VITE_IHERB_AFFILIATE_CODE || '';

// Build network configs with env vars
const getNetworkConfigs = () => {
  const amazon: NetworkConfig = {
    ...DEFAULT_NETWORKS.amazon,
    isConfigured: true, // Amazon always available
  };
  const iherb: NetworkConfig = {
    ...DEFAULT_NETWORKS.iherb,
    isConfigured: !!IHERB_AFFILIATE_CODE,
  };
  return { amazon, iherb };
};

/**
 * Generate search queries based on user profile for affiliate product search
 */
function generateAffiliateQueries(profile: any): string[] {
  const queries: string[] = [];
  
  // Priority 1: Use AI analysis keywords if available
  const aiKeywords = profile?.skin?.aiAnalysis?.keywords || profile?.aiAnalysis?.keywords || [];
  if (aiKeywords.length > 0) {
    // Combine first 3 keywords into a search query
    const keywordQuery = aiKeywords.slice(0, 3).join(' ') + ' skincare';
    queries.push(keywordQuery);
  }
  
  // Priority 2: Skin concerns
  const concerns = profile?.skin?.concerns || [];
  if (concerns.length > 0) {
    const concernsQuery = concerns.slice(0, 2).join(' ') + ' treatment skincare';
    queries.push(concernsQuery);
  }
  
  // Priority 3: Skin type
  const skinType = profile?.skin?.type || profile?.aiAnalysis?.skinType;
  if (skinType) {
    queries.push(`${skinType} skin type skincare routine`);
  }
  
  // Fallback
  if (queries.length === 0) {
    queries.push('korean skincare routine essentials');
  }
  
  return queries.slice(0, 3);
}

/**
 * Search for affiliate products via Google Search API (Amazon + iHerb with ratio-based distribution)
 * Distribution adapts to any number of slots (mobile: 2, desktop: 3, future: 4+)
 *
 * Optimized: batches calls by network (one API call per network instead of per slot)
 * and uses a shared 4-hour cache to avoid redundant searches for the same profile.
 */
async function searchAffiliateProducts(profile: any, totalSlots: number = 3, appLanguage?: string): Promise<any[]> {
  const queries = generateAffiliateQueries(profile);
  const profileCountry = profile?.identity?.country;
  const region: SupportedRegion = detectUserRegion(profileCountry);
  const regionConfig = getRegionConfig(region);
  const uiLang = appLanguage || (navigator.language?.startsWith('ko') ? 'ko' : navigator.language?.startsWith('pt') ? 'pt-BR' : navigator.language?.startsWith('es') ? 'es' : 'en');

  const regionalDomain = regionConfig.amazon?.domain ?? 'amazon.com';
  const { gl, hl } = getSearchParams(region, uiLang);

  // --- CACHE CHECK: return cached results if profile hasn't changed ---
  const skinType = profile?.skin?.type || profile?.aiAnalysis?.skinType || '';
  const skinConcerns = profile?.skin?.concerns || [];
  const aiKeywords = profile?.skin?.aiAnalysis?.keywords || profile?.aiAnalysis?.keywords || [];
  const aiFingerprint = aiKeywords.slice(0, 3).join(',');
  const cacheKey = buildProfileCacheKey('editorpicks', skinType, skinConcerns, region, uiLang, totalSlots, aiFingerprint);
  const cached = getCached<any[]>(cacheKey);
  if (cached) {
    return cached;
  }

  // Check auth before making API calls
  const headers = await getAuthHeaders();
  if (!headers['X-Firebase-Auth']) {
    return [];
  }

  const { amazon, iherb } = getNetworkConfigs();
  const affiliateCodes = {
    amazon: getAmazonTagForRegion(region),
    iherb: IHERB_AFFILIATE_CODE,
  };

  const distribution = distributeSlots({
    primary: amazon,
    secondary: [iherb],
    totalSlots,
  });

  // Extra `lr` param when user language differs from region default
  // Compare base subtags (e.g. 'pt' vs 'pt') since getSearchParams normalises hl
  // but regionConfig.hl may be a full tag like 'pt-BR'
  const extraParams = hl !== regionConfig.hl.split('-')[0] ? { lr: `lang_${uiLang.split('-')[0]}` } : {};

  // Helper: convert a Google Search result item into an editor pick
  const toItem = (item: any, networkName: 'amazon' | 'iherb'): any => {
    const isIherb = networkName === 'iherb';
    const productId = extractProductId(item.link, networkName) || `affiliate-${crypto.randomUUID()}`;
    const affiliateUrl = buildAffiliateUrl(item.link, networkName, affiliateCodes);
    const brand = isIherb ? 'iHerb' : 'Amazon';
    const affiliateTag = affiliateCodes[networkName];
    return {
      id: productId,
      title: item.title?.replace(/ - Amazon\.com.*$/i, '').replace(/ - iHerb$/i, '').trim() || 'Skincare Product',
      name: item.title?.replace(/ - Amazon\.com.*$/i, '').replace(/ - iHerb$/i, '').trim() || 'Skincare Product',
      brand,
      image: item.pagemap?.cse_image?.[0]?.src || item.pagemap?.cse_thumbnail?.[0]?.src || null,
      url: affiliateUrl,
      affiliateUrl,
      affiliateSource: networkName,
      trackingId: affiliateTag || undefined,
      isPaidPlacement: !!affiliateTag,
      reason: item.snippet?.slice(0, 100) || 'Recommended based on your skin profile',
      price: null,
    };
  };

  // --- BATCH: Group slots by network, one API call per network (parallel) ---
  const networkGroups = new Map<string, number[]>();
  for (let i = 0; i < Math.min(totalSlots, distribution.length); i++) {
    const networkName = distribution[i];
    if (!networkGroups.has(networkName)) {
      networkGroups.set(networkName, []);
    }
    networkGroups.get(networkName)!.push(i);
  }

  // If the first query returns 0 items, retry with alternate queries
  const networkPools = new Map<string, any[]>();
  await Promise.all(
    Array.from(networkGroups.entries()).map(async ([networkName, slotIndices]) => {
      const isIherb = networkName === 'iherb';
      const site = isIherb ? 'iherb.com' : regionalDomain;
      const num = Math.min(slotIndices.length * 2, 10);
      const startIndex = slotIndices[0] % queries.length;
      for (let attempt = 0; attempt < Math.min(queries.length, 3); attempt++) {
        const q = queries[(startIndex + attempt) % queries.length];
        try {
          const apiUrl = `${getApiBaseUrl()}/googleSearch`;
          const response = await nativeFetch(apiUrl, {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ query: q, num, siteRestrict: site, gl, hl, ...extraParams }),
          });
          if (!response.ok) { continue; }
          const data = await response.json();
          const items = (data.items || []).map((item: any) => toItem(item, networkName as 'amazon' | 'iherb'));
          if (items.length > 0) { networkPools.set(networkName, items); return; }
        } catch (err) {
          console.warn(`Batch affiliate search failed for ${networkName} (query ${attempt}):`, err);
        }
      }
      networkPools.set(networkName, []);
    }),
  );

  // If regional Amazon returned 0 results, fall back to amazon.com (global)
  const amazonPool = networkPools.get('amazon') || [];
  if (amazonPool.length === 0 && regionalDomain !== 'amazon.com') {
    try {
      const apiUrl = `${getApiBaseUrl()}/googleSearch`;
      const q = queries[0];
      const amazonSlots = networkGroups.get('amazon')?.length || 2;
      const response = await nativeFetch(apiUrl, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q, num: Math.min(amazonSlots * 2, 10), siteRestrict: 'amazon.com', gl, hl, ...extraParams }),
      });
      if (response.ok) {
        const data = await response.json();
        const items = (data.items || []).map((item: any) => toItem(item, 'amazon'));
        networkPools.set('amazon', items);
      }
    } catch { /* fallback failed, continue with empty pool */ }
  }

  // Fill slots from pools (dedup by URL, fallback iherb→amazon)
  const seenUrls = new Set<string>();
  const results: any[] = [];
  for (let i = 0; i < Math.min(totalSlots, distribution.length); i++) {
    const networkName = distribution[i] as 'amazon' | 'iherb';
    const pool = networkPools.get(networkName) || [];
    const pick = pool.find((r: any) => !seenUrls.has(r.affiliateUrl));
    if (pick) {
      seenUrls.add(pick.affiliateUrl);
      results.push(pick);
      pool.splice(pool.indexOf(pick), 1);
    } else if (networkName === 'iherb') {
      const fallbackPool = networkPools.get('amazon') || [];
      const fallback = fallbackPool.find((r: any) => !seenUrls.has(r.affiliateUrl));
      if (fallback) {
        seenUrls.add(fallback.affiliateUrl);
        results.push(fallback);
        fallbackPool.splice(fallbackPool.indexOf(fallback), 1);
      }
    }
  }

  // --- CACHE STORE ---
  if (results.length > 0) {
    setCached(cacheKey, results);
  }

  return results;
}

export function useEditorPicks({
  masterProfile,
  catalog,
  isLoggedIn,
  setProfileError,
  monetizationMode,
  useRag = true,
}: UseEditorPicksParams): UseEditorPicksReturn {
  const { t: ti18n, i18n } = useTranslation('common');

  // Resolve effective mode (param > constant)
  const effectiveMode = monetizationMode ?? MONETIZATION_MODE;
  
  // State
  const [editorPicks, setEditorPicks] = useState<any[]>([]);
  const [isEditorPicksLoading, setIsEditorPicksLoading] = useState(false);
  
  // Track if current picks are from affiliate
  const isAffiliate = effectiveMode === 'affiliate';
  
  // Ref for delayed binding (used by useWebSearch)
  const handleRefreshEditorPicksRef = useRef<((profileOverride?: any) => Promise<void>) | null>(null);

  /**
   * Refresh editor picks based on profile and catalog
   * - Called manually or auto-triggered for Phase 2 users
   * - In AFFILIATE mode: Fetches Amazon products via Google Search API
   * - In INHOUSE mode: Ranks products using AI-based scoring from profileService
   * - Tracks analytics for recommendation views
   */
  const handleRefreshEditorPicks = useCallback(async (profileOverride?: any) => {
    const profileForRanking = profileOverride 
      ? ensureShape(profileOverride)
      : ensureShape(masterProfile || {});
    
    setIsEditorPicksLoading(true);
    try {
      let picks: any[] = [];
      
      // AFFILIATE MODE: Fetch from Amazon via Google Search
      // Retry once after a short delay if the first attempt returns empty (cold-start resilience)
      if (effectiveMode === 'affiliate') {
        try {
          picks = await searchAffiliateProducts(profileForRanking, 3, i18n.language);
        } catch {
          // Retry once after a short delay on error (cold-start resilience)
          await new Promise(r => setTimeout(r, 3000));
          picks = await searchAffiliateProducts(profileForRanking, 3, i18n.language);
        }
        setProfileError(null);
      } 
      // INHOUSE MODE: Use in-house catalog ranking
      else {
        // Check if catalog is loaded
        if (!catalog || catalog.length === 0) {
          console.warn('Catalog is empty, cannot generate recommendations');
          setProfileError(ti18n('errors.catalogLoadFailed'));
          setEditorPicks([]);
          return;
        }
        
        // Compute embedding similarity map if RAG is enabled and embeddings are available
        let embeddingSimilarityMap: Map<string, number> | undefined;
        if (useRag && hasEmbeddings()) {
          try {
            const profileEmbedding = await getProfileEmbedding(profileForRanking);
            if (profileEmbedding) {
              embeddingSimilarityMap = getEmbeddingSimilarityMap(profileEmbedding);
            }
          } catch (err) {
            console.warn('⚠️ Embedding similarity unavailable:', err);
          }
        }
        // rankEditorPicks takes (profile, catalog, topK, embeddingSimilarityMap) and returns EditorPick[] directly
        picks = rankEditorPicks(profileForRanking, catalog, 3, embeddingSimilarityMap);
        
        setProfileError(null);
      }
      
      setEditorPicks(Array.isArray(picks) ? picks : []);
      
      // Track recommendation view for analytics
      const currentUser = auth.currentUser;
      if (currentUser?.uid && Array.isArray(picks) && picks.length > 0) {
        trackRecommendationViewed(currentUser.uid, picks).catch(err => 
          console.warn('⚠️ Failed to track recommendation view:', err)
        );
      }
    } catch (error) {
      console.error('Failed to refresh editor picks:', error);
      // Empty fallback if ranking fails
      setEditorPicks([]);
      setProfileError(ti18n('errors.recommendationsFetchFailed'));
    } finally {
      setIsEditorPicksLoading(false);
    }
  }, [masterProfile, catalog, ti18n, setProfileError, effectiveMode, useRag]);

  // Update ref for delayed binding (useWebSearch hook uses this)
  useEffect(() => {
    handleRefreshEditorPicksRef.current = handleRefreshEditorPicks;
  }, [handleRefreshEditorPicks]);

  // Auto-refresh editor picks for Phase 2 users (AI analysis complete + 50% profile)
  useEffect(() => {
    // For affiliate mode, we don't need catalog - just need profile
    if (!isLoggedIn || !masterProfile) return;
    
    // For inhouse mode, also need catalog
    if (effectiveMode === 'inhouse' && (!catalog || catalog.length === 0)) return;
    
    const hasAIAnalysis = !!(masterProfile?.aiAnalysis?.skinType || masterProfile?.aiAnalysis?.lastAnalysisDate);
    const completion = calculateProfileCompletion(masterProfile);
    const isPhase2 = hasAIAnalysis && completion >= 50;
    
    if (isPhase2) {
      handleRefreshEditorPicks(masterProfile);
    }
  // Only trigger on specific field changes to avoid infinite loops
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterProfile?.aiAnalysis?.lastAnalysisDate, masterProfile?.logistics?.address1, masterProfile?.skin?.concerns, isLoggedIn, catalog, i18n.language]);

  return {
    editorPicks,
    setEditorPicks,
    isEditorPicksLoading,
    handleRefreshEditorPicks,
    handleRefreshEditorPicksRef,
    isAffiliate,
  };
}
